<template>
  <IntercomLayout>
    <template #topbar>
      <SevaTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="donations-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('donation.title') }}</h1>
          <p class="page-subtitle">{{ $t('donation.subtitle') }}</p>
        </div>
        <IntercomButton variant="primary" @click="showAddModal = true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>{{ $t('donation.addDonation') }}</span>
        </IntercomButton>
      </div>

      <!-- Filters -->
      <div class="filters-row">
        <IntercomInput
          v-model="searchQuery"
          :placeholder="$t('search.donationsPlaceholder')"
          type="search"
          class="search-input"
          @input="handleSearch"
        />
        <IntercomSelect
          v-model="filterType"
          :options="typeFilterOptions"
          :placeholder="$t('filters.allTypes')"
          class="filter-select"
          @update:model-value="handleFilterType"
        />
      </div>

      <!-- Donations Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="donations"
          :loading="isLoading"
          hoverable
          clickable
          @row-click="handleRowClick"
        >
          <template #cell-donation_type="{ value }">
            <IntercomBadge :variant="getTypeVariant(value)">
              {{ value ? $t(`donation.types.${value}`) : '' }}
            </IntercomBadge>
          </template>
          <template #cell-amount="{ value, row }">
            <template v-if="row.donation_type === 'volunteer'">{{ row.volunteer_hours || 0 }} hrs</template>
            <template v-else>{{ formatCompactCurrency(value || 0) }}</template>
          </template>
          <template #cell-donation_date="{ value }">
            {{ formatDate(value) }}
          </template>
        </IntercomTable>
        <p v-if="!isLoading && donations.length === 0" class="empty-text">
          {{ $t('donation.noDonations') }}
        </p>
      </IntercomCard>

      <IntercomPagination
        :offset="offset"
        :total="total"
        :limit="limit"
        @update:offset="(v) => { offset.value = v; fetchDonations() }"
      />

      <!-- Add/Edit Modal -->
      <IntercomModal
        v-model:show="showAddModal"
        :title="createdDonation ? $t('receipt.donationSuccess') : (editingDonation ? $t('donation.editDonation') : $t('donation.addDonation'))"
        :width="createdDonation ? '900px' : null"
      >
        <!-- Success State with Print Preview -->
        <div v-if="createdDonation" class="success-container">
          <div class="success-header">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#10B981" stroke-width="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M9 12l2 2 4-4" />
            </svg>
            <h2>{{ $t('receipt.donationSuccess') }}</h2>
            <p class="receipt-number">{{ createdDonation.receipt_number || formatReceiptNumber(createdDonation) }}</p>
          </div>

          <div class="print-preview-container" ref="receiptContainer">
            <DonationReceiptPrint
              :donation="createdDonation"
              :patron="selectedPatron"
              :business-name="businessName"
              :business-address="trustSettings.trust_address || ''"
              :trust-name="trustSettings.trust_name || ''"
              :trust-pan="trustSettings.trust_pan || ''"
              :trust-80g-registration="trustSettings.trust_80g_registration || ''"
              :trust-80g-din="trustSettings.trust_80g_din || ''"
              :trust-80g-valid-from="trustSettings.trust_80g_valid_from || ''"
              :trust-80g-valid-to="trustSettings.trust_80g_valid_to || ''"
              :trust-12a-number="trustSettings.trust_12a_number || ''"
            />
          </div>

          <div class="success-actions">
            <IntercomButton variant="primary" @click="handlePrint">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              {{ $t('receipt.printReceipt') }}
            </IntercomButton>
            <IntercomButton variant="secondary" @click="handleDownloadPdf" :loading="isDownloading">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download PDF
            </IntercomButton>
            <IntercomButton variant="secondary" @click="closeSuccessAndViewDonation">
              {{ $t('common.view') }}
            </IntercomButton>
            <IntercomButton variant="ghost" @click="closeModal">
              {{ $t('common.close') }}
            </IntercomButton>
          </div>
        </div>

        <!-- Form State -->
        <DonationForm
          v-else
          :donation="editingDonation"
          :patrons="patrons"
          :bank-accounts="bankAccounts"
          :loading="isSaving"
          :default-patron-id="initialPatronId"
          @submit="handleSave"
          @cancel="closeModal"
          @patron-created="handlePatronCreated"
        />
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { usePrint } from '@shared/composables/usePrint.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { usePaginatedList } from '@shared/composables/usePaginatedList.js'
import { useSeva } from '../composables/useSeva.js'
import { formatCompactCurrency } from '@shared/utils/currency.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomBadge,
  IntercomInput,
  IntercomSelect,
  IntercomModal,
  IntercomPagination,
} from '@shared/components/intercom'
import SevaTopbar from '../components/SevaTopbar.vue'
import DonationForm from '../components/DonationForm.vue'
import DonationReceiptPrint from '../components/print/DonationReceiptPrint.vue'
import { useNavigation } from '../composables/useNavigation.js'
import dayjs from 'dayjs'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const toast = useToast()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { listDonations, listPatrons, listBankAccounts, createDonation, updateDonation, getKVSettings } = useSeva()
const { navSections } = useNavigation()

// State
const patrons = ref([])
const bankAccounts = ref([])
const isSaving = ref(false)
const searchQuery = ref('')
const filterType = ref(null)
const showAddModal = ref(false)
const editingDonation = ref(null)
const createdDonation = ref(null)
const selectedPatron = ref(null)
const trustSettings = ref({})
const initialPatronId = ref(null)
const receiptContainer = ref(null)
const isDownloading = ref(false)

// Paginated list
const {
  items: donations,
  total,
  loading: isLoading,
  offset,
  limit,
  fetch: fetchDonations,
  resetAndFetch,
} = usePaginatedList(
  (params) => listDonations({
    ...params,
    ...(searchQuery.value ? { search: searchQuery.value } : {}),
    ...(filterType.value ? { type: filterType.value } : {}),
  }),
  { immediate: false }
)

const formatDate = (date) => date ? dayjs(date).format('DD MMM YYYY') : ''

const formatReceiptNumber = (donation) => {
  const id = donation?.id || '000'
  const date = dayjs(donation?.donation_date || new Date()).format('YYYYMMDD')
  return `DON-${date}-${String(id).padStart(4, '0')}`
}

const typeFilterOptions = computed(() => [
  { value: null, label: t('filters.allTypes') },
  { value: 'money', label: t('donation.types.money') },
  { value: 'in_kind', label: t('donation.types.in_kind') },
  { value: 'volunteer', label: t('donation.types.volunteer') },
])

const columns = [
  { key: 'donation_number', title: t('donation.number'), width: '120px' },
  { key: 'patron_name', title: t('donation.patron'), width: '180px' },
  { key: 'donation_type', title: t('donation.type'), width: '100px' },
  { key: 'amount', title: t('donation.amount'), width: '120px', align: 'right' },
  { key: 'donation_date', title: t('donation.date'), width: '120px' },
]

const getTypeVariant = (type) => {
  switch (type) {
    case 'money': return 'success'
    case 'in_kind': return 'info'
    case 'volunteer': return 'warning'
    default: return 'default'
  }
}

const fetchPatrons = async () => {
  try {
    const response = await listPatrons({ limit: 100 })
    patrons.value = response?.data || []
  } catch (error) {
    console.error('Failed to fetch patrons:', error)
  }
}

const fetchBankAccounts = async () => {
  try {
    const response = await listBankAccounts()
    bankAccounts.value = response?.accounts || []
  } catch (error) {
    console.error('Failed to fetch bank accounts:', error)
  }
}

const handleSearch = () => {
  resetAndFetch()
}

const handleFilterType = () => {
  resetAndFetch()
}

const handleRowClick = (donation) => {
  router.push(`/donations/${donation.id}`)
}

const handleSave = async (formData) => {
  isSaving.value = true
  try {
    if (editingDonation.value) {
      await updateDonation(editingDonation.value.id, formData)
      toast.success(t('success.donationUpdated'))
      closeModal()
    } else {
      const response = await createDonation(formData)
      const donation = response?.donation || response
      // Ensure form data is preserved for receipt (API might not return all fields)
      if (!donation.donation_date) {
        donation.donation_date = formData.donation_date
      }
      if (!donation.donation_type) {
        donation.donation_type = formData.donation_type
      }
      // Find the patron for the receipt
      selectedPatron.value = patrons.value.find(p => p.id === formData.patron_id) || null
      // Store created donation for success state with print preview
      createdDonation.value = donation
    }
    fetchDonations()
  } catch (error) {
    console.error('Failed to save donation:', error)
    toast.error(error.detail || error.message || t('errors.saveFailed'))
  } finally {
    isSaving.value = false
  }
}

const closeModal = () => {
  showAddModal.value = false
  editingDonation.value = null
  createdDonation.value = null
  selectedPatron.value = null
  initialPatronId.value = null
}

// Print — server-side PDF for donations will be added later
// For now, use browser print dialog
const { printDirect: handlePrint } = usePrint()

const handleDownloadPdf = async () => {
  // TODO: Replace with server-side PDF endpoint (GET /v1/seva/donations/{id}/pdf)
  // For now, trigger browser print which allows "Save as PDF"
  handlePrint()
}

const closeSuccessAndViewDonation = () => {
  const donationId = createdDonation.value?.id
  closeModal()
  if (donationId) {
    router.push(`/donations/${donationId}`)
  }
}

const fetchTrustSettings = async () => {
  try {
    const response = await getKVSettings()
    trustSettings.value = response?.data || response || {}
  } catch (error) {
    console.error('Failed to fetch trust settings:', error)
  }
}

const handlePatronCreated = (newPatron) => {
  // Add the newly created patron to the local list
  patrons.value = [...patrons.value, newPatron]
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

onMounted(() => {
  fetchBusiness()
  fetchDonations()  // usePaginatedList fetch
  fetchPatrons()
  fetchBankAccounts()
  fetchTrustSettings()

  // Auto-open modal if ?new=true query param is present (from dashboard or patron detail)
  if (route.query.new === 'true') {
    if (route.query.patron_id) {
      initialPatronId.value = Number(route.query.patron_id)
    }
    showAddModal.value = true
  }
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.donations-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

.filters-row {
  display: flex;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.search-input {
  flex: 1;
  max-width: 400px;
}

.filter-select {
  width: 180px;
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-8);
}

/* Success state styles */
.success-container {
  padding: var(--space-4);
}

.success-header {
  text-align: center;
  padding: var(--space-4);
  background: var(--color-green-50);
  border-radius: var(--radius-lg);
  margin-bottom: var(--space-4);
}

.success-header svg {
  margin-bottom: var(--space-2);
}

.success-header h2 {
  margin: 0 0 var(--space-1) 0;
  color: var(--color-green-700);
  font-size: var(--font-size-lg);
}

.success-header .receipt-number {
  margin: 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-600);
}

.print-preview-container {
  background: #f5f5f5;
  border-radius: var(--radius-lg);
  padding: var(--space-4);
  margin-bottom: var(--space-4);
  max-height: 50vh;
  overflow: auto;
  display: flex;
  justify-content: center;
}

.success-actions {
  display: flex;
  justify-content: center;
  gap: var(--space-3);
  flex-wrap: wrap;
}

.success-actions button {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

/* Print media queries */
@media print {
  .donations-view,
  .page-header,
  .filters-row,
  nav,
  aside,
  header,
  .success-header,
  .success-actions {
    display: none !important;
  }

  .print-preview-container {
    max-height: none;
    overflow: visible;
    padding: 0;
    background: white;
    border-radius: 0;
  }

  body {
    margin: 0;
    padding: 0;
  }
}

@media (max-width: 768px) {
  .donations-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .filters-row {
    flex-direction: column;
  }

  .search-input,
  .filter-select {
    max-width: none;
    width: 100%;
  }

  .success-actions {
    flex-direction: column;
  }

  .success-actions button {
    width: 100%;
    justify-content: center;
  }
}
</style>
